/**
 * Mod Index Service
 * Intelligent matching and filtering service for mods
 */

import modRegistry, { getAllMods, getModById, getModsForGame as registryGetModsForGame } from '../data/modRegistry';

/**
 * Get all mods for a specific game with full details
 * @param {string} gameId - The game identifier
 * @returns {Array} Array of mod objects
 */
export function getModsForGame(gameId) {
    return registryGetModsForGame(gameId);
}

/**
 * Filter mods based on multiple criteria
 * @param {Object} criteria - Filter criteria
 * @param {string} criteria.gameId - Optional: Filter by game
 * @param {string} criteria.category - Optional: Filter by category (gameplay, combat, visual, resources)
 * @param {string} criteria.type - Optional: Filter by type (toggle, slider, dropdown)
 * @param {string} criteria.platform - Optional: Filter by platform (Steam, Epic Games, GOG)
 * @param {Array<string>} criteria.tags - Optional: Filter by tags
 * @param {number} criteria.minPopularity - Optional: Minimum popularity score (0-100)
 * @returns {Array} Filtered array of mods
 */
export function filterMods(criteria = {}) {
    let mods = getAllMods();

    // Filter by game
    if (criteria.gameId) {
        mods = mods.filter(mod => mod.supportedGames.includes(criteria.gameId));
    }

    // Filter by category
    if (criteria.category) {
        mods = mods.filter(mod => mod.category === criteria.category);
    }

    // Filter by type
    if (criteria.type) {
        mods = mods.filter(mod => mod.type === criteria.type);
    }

    // Filter by platform
    if (criteria.platform) {
        mods = mods.filter(mod => mod.platforms.includes(criteria.platform));
    }

    // Filter by tags (must have ALL specified tags)
    if (criteria.tags && criteria.tags.length > 0) {
        mods = mods.filter(mod =>
            criteria.tags.every(tag => mod.tags.includes(tag))
        );
    }

    // Filter by minimum popularity
    if (criteria.minPopularity !== undefined) {
        mods = mods.filter(mod => mod.popularityScore >= criteria.minPopularity);
    }

    return mods;
}

/**
 * Search mods by name or description (fuzzy matching)
 * @param {string} query - Search query
 * @param {string} gameId - Optional: Limit search to specific game
 * @returns {Array} Array of matching mods, sorted by relevance
 */
export function searchMods(query, gameId = null) {
    if (!query || query.trim() === '') {
        return gameId ? getModsForGame(gameId) : getAllMods();
    }

    const searchTerm = query.toLowerCase().trim();
    let mods = gameId ? getModsForGame(gameId) : getAllMods();

    // Score each mod based on match quality
    const scoredMods = mods.map(mod => {
        let score = 0;
        const name = mod.name.toLowerCase();
        const description = mod.description.toLowerCase();

        // Exact name match (highest priority)
        if (name === searchTerm) {
            score += 100;
        }
        // Name starts with query
        else if (name.startsWith(searchTerm)) {
            score += 50;
        }
        // Name contains query
        else if (name.includes(searchTerm)) {
            score += 25;
        }

        // Description contains query
        if (description.includes(searchTerm)) {
            score += 10;
        }

        // Tag matches
        if (mod.tags.some(tag => tag.toLowerCase().includes(searchTerm))) {
            score += 15;
        }

        // Category matches
        if (mod.category.toLowerCase().includes(searchTerm)) {
            score += 5;
        }

        return { mod, score };
    });

    // Filter out non-matches and sort by score
    return scoredMods
        .filter(item => item.score > 0)
        .sort((a, b) => b.score - a.score)
        .map(item => item.mod);
}

/**
 * Get mods for a game grouped by category
 * @param {string} gameId - The game identifier
 * @returns {Object} Object with categories as keys and mod arrays as values
 */
export function getModsByCategory(gameId) {
    const mods = getModsForGame(gameId);
    const categorized = {
        gameplay: [],
        combat: [],
        resources: [],
        visual: []
    };

    mods.forEach(mod => {
        if (categorized[mod.category]) {
            categorized[mod.category].push(mod);
        }
    });

    return categorized;
}

/**
 * Get most popular mods across all games
 * @param {number} limit - Number of mods to return
 * @param {string} gameId - Optional: Limit to specific game
 * @returns {Array} Array of top mods sorted by popularity
 */
export function getPopularMods(limit = 10, gameId = null) {
    const mods = gameId ? getModsForGame(gameId) : getAllMods();

    return mods
        .sort((a, b) => b.popularityScore - a.popularityScore)
        .slice(0, limit);
}

/**
 * Check if a mod is compatible with a game and platform
 * @param {string} modId - The mod identifier
 * @param {string} gameId - The game identifier
 * @param {string} platform - Optional: The platform (Steam, Epic Games, GOG)
 * @returns {Object} Compatibility info { compatible: boolean, reason: string }
 */
export function getModCompatibility(modId, gameId, platform = null) {
    const mod = getModById(modId);

    if (!mod) {
        return { compatible: false, reason: 'Mod nicht gefunden' };
    }

    // Check game compatibility
    if (!mod.supportedGames.includes(gameId)) {
        return { compatible: false, reason: 'Spiel wird nicht unterstützt' };
    }

    // Check platform compatibility if specified
    if (platform && !mod.platforms.includes(platform)) {
        return { compatible: false, reason: `Plattform ${platform} wird nicht unterstützt` };
    }

    return { compatible: true, reason: 'Kompatibel' };
}

/**
 * Get mod statistics for a game
 * @param {string} gameId - The game identifier
 * @returns {Object} Statistics object
 */
export function getModStats(gameId) {
    const mods = getModsForGame(gameId);

    const stats = {
        total: mods.length,
        byCategory: {
            gameplay: 0,
            combat: 0,
            resources: 0,
            visual: 0
        },
        byType: {
            toggle: 0,
            slider: 0,
            dropdown: 0
        },
        averagePopularity: 0,
        popularTags: {}
    };

    let totalPopularity = 0;

    mods.forEach(mod => {
        // Count by category
        if (stats.byCategory[mod.category] !== undefined) {
            stats.byCategory[mod.category]++;
        }

        // Count by type
        if (stats.byType[mod.type] !== undefined) {
            stats.byType[mod.type]++;
        }

        // Sum popularity
        totalPopularity += mod.popularityScore;

        // Count tags
        mod.tags.forEach(tag => {
            stats.popularTags[tag] = (stats.popularTags[tag] || 0) + 1;
        });
    });

    // Calculate average popularity
    stats.averagePopularity = mods.length > 0
        ? Math.round(totalPopularity / mods.length)
        : 0;

    return stats;
}

/**
 * Get all available categories
 * @returns {Array} Array of category objects with counts
 */
export function getCategories() {
    const allMods = getAllMods();
    const categories = {};

    allMods.forEach(mod => {
        if (!categories[mod.category]) {
            categories[mod.category] = {
                id: mod.category,
                name: getCategoryDisplayName(mod.category),
                count: 0
            };
        }
        categories[mod.category].count++;
    });

    return Object.values(categories);
}

/**
 * Get display name for category
 * @param {string} category - Category ID
 * @returns {string} Display name
 */
function getCategoryDisplayName(category) {
    const names = {
        gameplay: 'Gameplay',
        combat: 'Kampf',
        resources: 'Ressourcen',
        visual: 'Visuelle Effekte'
    };
    return names[category] || category;
}

/**
 * Get all available tags with counts
 * @returns {Array} Array of tag objects
 */
export function getTags() {
    const allMods = getAllMods();
    const tags = {};

    allMods.forEach(mod => {
        mod.tags.forEach(tag => {
            if (!tags[tag]) {
                tags[tag] = {
                    id: tag,
                    name: tag,
                    count: 0
                };
            }
            tags[tag].count++;
        });
    });

    return Object.values(tags).sort((a, b) => b.count - a.count);
}

export default {
    getModsForGame,
    filterMods,
    searchMods,
    getModsByCategory,
    getPopularMods,
    getModCompatibility,
    getModStats,
    getCategories,
    getTags
};
